import { useEffect, useMemo } from 'react'
import { Settings2, FileText, RefreshCw } from 'lucide-react'

import type { ValidationStageKey } from '@ispot/shared'

import { useJourneyStore } from '@/stores/journeyStore'
import { useSessionStore } from '@/stores/sessionStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import {
  useLatestCompletedAnalysis,
  useStartValidationAnalysis,
} from '@/hooks/useValidationAnalysis'
import { ValidationRadar } from '@/components/validation/ValidationRadar'
import { classifyBand } from '@/components/validation/ValidationScoreBand'
import { DomainSummaryCards } from '../dashboard/DomainSummaryCards'
import { RecommendationsList } from '../dashboard/RecommendationsList'

const STAGES = [
  { key: 'pe' as ValidationStageKey, shortName: 'Problem', name: 'Problem existence' },
  { key: 'pu' as ValidationStageKey, shortName: 'Urgency', name: 'Problem urgency' },
  { key: 'psf' as ValidationStageKey, shortName: 'Solution', name: 'Problem–solution fit' },
  { key: 'bmf' as ValidationStageKey, shortName: 'Business', name: 'Business model fit' },
  { key: 'pmf' as ValidationStageKey, shortName: 'Market', name: 'Product–market fit' },
]

interface OverviewModeProps {
  onViewChange?: (view: string) => void
}

export function OverviewMode({ onViewChange }: OverviewModeProps) {
  const { accountId, projectId } = useSessionStore()
  const { setShowGoalWizard } = useWorkspaceStore()
  const {
    isLoading,
    fetchJourney,
    getSelectedGoal,
    getActivePhases,
    getCurrentPhase,
    getPhaseProgress,
    getOverallProgress,
  } = useJourneyStore()

  const { data: analysis } = useLatestCompletedAnalysis()
  const startAnalysis = useStartValidationAnalysis()

  useEffect(() => {
    if (accountId && projectId) {
      void fetchJourney(accountId, projectId)
    }
  }, [accountId, projectId, fetchJourney])

  const goal = getSelectedGoal()
  const phases = getActivePhases()
  const currentPhase = getCurrentPhase()
  const overallProgress = Math.round(getOverallProgress())

  const scores = useMemo(
    () =>
      STAGES.reduce(
        (acc, s) => {
          const found = analysis?.stageScores?.find((x) => x.stageKey === s.key)
          acc[s.key] = found?.confidenceScore ?? 0
          return acc
        },
        {} as Record<ValidationStageKey, number>,
      ),
    [analysis],
  )

  const overall = useMemo(() => {
    const vals = STAGES.map((s) => scores[s.key] ?? 0)
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length)
  }, [scores])

  const { strongest, weakest } = useMemo(() => {
    const sorted = [...STAGES].sort((a, b) => (scores[b.key] ?? 0) - (scores[a.key] ?? 0))
    return { strongest: sorted[0], weakest: sorted[sorted.length - 1] }
  }, [scores])

  const band = analysis ? classifyBand(overall) : null
  const tone = band ? { stroke: band.color, fill: band.fillColor } : undefined

  const lastAnalyzed = analysis?.completedAt
    ? new Date(analysis.completedAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null

  return (
    <div className="h-full overflow-y-auto">
      <div style={{ padding: '28px 5% 44px' }}>

        {/* ── Top row: 3 columns — journey | radar | PMF text ── */}
        <div
          className="grid mb-6"
          style={{
            gridTemplateColumns: '1fr 720px 1fr',
            gap: 32,
            paddingBottom: 28,
          }}
        >
          {/* ── Col 1: Journey ── */}
          <div style={{ borderRight: '1px solid var(--color-border)', paddingRight: 32 }}>
            <div
              className="font-mono uppercase text-text-muted"
              style={{ fontSize: 9.5, letterSpacing: '0.12em', marginBottom: 14 }}
            >
              Your Journey
            </div>

            {!goal ? (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-text-muted">No journey selected.</p>
                <button
                  onClick={() => setShowGoalWizard(true)}
                  className="inline-flex w-fit items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--color-accent)] rounded-lg hover:bg-[var(--color-accent-700)] transition-colors"
                >
                  Choose journey
                </button>
              </div>
            ) : isLoading ? (
              <div className="flex flex-col gap-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-9 bg-warm-200 animate-pulse rounded" />
                ))}
              </div>
            ) : (
              <>
                <div className="flex flex-col">
                  {phases.map((phase, idx) => {
                    const progress = getPhaseProgress(phase.id)
                    const isCurrent = phase.id === currentPhase?.id
                    const isDone = progress >= 100

                    return (
                      <div
                        key={phase.id}
                        className={[
                          'flex items-center gap-3 py-2.5',
                          idx < phases.length - 1 ? 'border-b border-border' : '',
                        ].join(' ')}
                      >
                        <div
                          className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                          style={{
                            background: isDone ? 'var(--color-text)' : isCurrent ? phase.color : 'transparent',
                            border: isDone || isCurrent ? 'none' : '1.5px solid var(--color-border-strong)',
                            fontFamily: 'var(--font-mono)',
                            fontSize: 10,
                            fontWeight: 500,
                            color: isDone || isCurrent ? '#fff' : 'var(--color-text-muted)',
                          }}
                        >
                          {idx + 1}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div
                            className="leading-tight"
                            style={{
                              fontSize: 13,
                              fontWeight: isCurrent || isDone ? 500 : 400,
                              color: isCurrent || isDone ? 'var(--color-text)' : 'var(--color-text-secondary)',
                            }}
                          >
                            {phase.name}
                          </div>
                          {isCurrent && !isDone && progress > 0 && (
                            <div className="mt-1 h-[2px] rounded-full overflow-hidden bg-warm-200" style={{ width: 64 }}>
                              <div
                                className="h-full rounded-full"
                                style={{ width: `${progress}%`, backgroundColor: phase.color }}
                              />
                            </div>
                          )}
                        </div>

                        {(isDone || (isCurrent && progress > 0)) && (
                          <span className="flex-shrink-0 font-mono tabular-nums text-text-muted" style={{ fontSize: 11 }}>
                            {isDone ? 'Done' : `${Math.round(progress)}%`}
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>

                <div className="flex items-center gap-2 mt-4 pt-1">
                  <span className="font-mono tabular-nums text-text-muted" style={{ fontSize: 11 }}>
                    Journey · {overallProgress}%
                  </span>
                  <button
                    onClick={() => setShowGoalWizard(true)}
                    className="flex items-center gap-1 text-text-secondary hover:text-text transition-colors"
                    style={{ fontSize: 11 }}
                  >
                    <Settings2 className="w-3 h-3" />
                    Change
                  </button>
                </div>
              </>
            )}
          </div>

          {/* ── Col 2: Radar chart ── */}
          <div
            className="flex items-center justify-center"
            style={{ borderRight: '1px solid var(--color-border)', paddingRight: 32 }}
          >
            {analysis && tone ? (
              <ValidationRadar
                stages={STAGES}
                scores={scores}
                selectedStage={null}
                onSelectStage={() => {}}
                tone={tone}
              />
            ) : (
              <div className="flex flex-col items-center gap-3 py-8">
                <p className="text-sm text-text-muted text-center">No validation data yet.</p>
                <button
                  type="button"
                  onClick={() => startAnalysis.mutate()}
                  disabled={startAnalysis.isPending}
                  className="inline-flex items-center gap-1.5 rounded-md bg-text text-white hover:bg-[#14242e] disabled:opacity-50 transition-colors"
                  style={{ padding: '6px 12px', fontSize: 12, fontWeight: 500 }}
                >
                  <RefreshCw className={`w-3 h-3 ${startAnalysis.isPending ? 'animate-spin' : ''}`} />
                  Run analysis
                </button>
              </div>
            )}
          </div>

          {/* ── Col 3: PMF text + Recommendations ── */}
          <div className="flex flex-col gap-4">
            {analysis && band ? (
              <>
                <div>
                  <div className="font-mono uppercase text-text-muted" style={{ fontSize: 9.5, letterSpacing: '0.12em', marginBottom: 10 }}>
                    Validation analysis{lastAnalyzed ? ` · ${lastAnalyzed}` : ''}
                  </div>
                  <h2
                    className="text-text"
                    style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.014em' }}
                  >
                    Sits in the{' '}
                    <em className="not-italic font-semibold" style={{ color: band.color }}>
                      {band.label}
                    </em>{' '}
                    band
                  </h2>
                  <p className="text-text-secondary mt-2" style={{ fontSize: 13, lineHeight: 1.6 }}>
                    Overall confidence score of{' '}
                    <strong className="text-text font-medium">{overall}%</strong>.<br />
                    Strongest on{' '}
                    <strong className="text-text font-medium">{strongest.name}</strong> at {scores[strongest.key]}%.<br />
                    Weakest on{' '}
                    <strong className="text-text font-medium">{weakest.name}</strong> at {scores[weakest.key]}%.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {analysis.artifactId && (
                    <button
                      type="button"
                      onClick={() => onViewChange?.('analysis')}
                      className="inline-flex items-center gap-1.5 rounded-md bg-bg-card text-text transition-colors hover:bg-bg-secondary border border-border-strong"
                      style={{ padding: '6px 12px', fontSize: 12, fontWeight: 500 }}
                    >
                      <FileText className="w-3 h-3" />
                      View report
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => startAnalysis.mutate()}
                    disabled={startAnalysis.isPending}
                    className="inline-flex items-center gap-1.5 rounded-md bg-text text-white hover:bg-[#14242e] disabled:opacity-50 transition-colors"
                    style={{ padding: '6px 12px', fontSize: 12, fontWeight: 500 }}
                  >
                    <RefreshCw className={`w-3 h-3 ${startAnalysis.isPending ? 'animate-spin' : ''}`} />
                    Re-analyze
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => onViewChange?.('analysis')}
                  className="inline-flex items-center gap-1 w-fit text-text-secondary hover:text-text transition-colors"
                  style={{ fontSize: 12, fontWeight: 500 }}
                >
                  View full validation report
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                    <path d="M2 5.5h7M6 3l2.5 2.5L6 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </>
            ) : (
              <div>
                <div className="font-mono uppercase text-text-muted" style={{ fontSize: 9.5, letterSpacing: '0.12em', marginBottom: 10 }}>
                  Validation analysis
                </div>
                <p className="text-sm text-text-muted">Run an analysis to see your PMF score across all five validation dimensions.</p>
              </div>
            )}

          </div>
        </div>

        {/* Domains */}
        <div className="mb-6">
          <DomainSummaryCards onViewChange={onViewChange} />
        </div>

        {/* Recommendations */}
        <RecommendationsList />

      </div>
    </div>
  )
}
